'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';

import { Text, FlagIcon, useTranslations } from '@northladder/i18n';
import { ITenantEnvironment } from '@northladder/services';
import { TTextLocalized } from '@northladder/utilities';

import { CircularProgressLoader } from '../../components';
import { cn } from '../../lib';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../ui';

import { RegionPickerFallback } from './RegionPickerFallback';

interface IRegionPickerProps {
  className?: string;
  hasError?: TTextLocalized | null;
  htmlForId: string;
  /**
   * This renders the default selected instance passed to this dropdown.
   */
  initialRegion: ITenantEnvironment | null;
  isEnabled?: boolean;
  /**
   * This states initial loading state without cached data.
   */
  isLoading?: boolean;
  /**
   * This states the background regions fetch to update cache.
   */
  isFetching?: boolean;
  /**
   * Allow the keyboard arrow keys to loop through the options
   */
  isLoopingEnabled?: boolean;
  items?: ITenantEnvironment[] | null;
  /**
   * This renders the default selected instance passed to this dropdown.
   */
  label?: TTextLocalized;
  /**
   * Provides a callback to refetch regions if the previous attempt failed.
   */
  onRefetchRegions: () => void;
  /**
   * Callback supplied to the parent component to provide the selected country.
   */
  onSelectRegion: (selectedRegion: ITenantEnvironment | null) => void;
}

/**
 * -----------------------------------------------------------------------------
 * This component renders a dropdown for selecting a region.
 * - Fetches the list of regions from the remote tenant configuration
 * - Displays a loading state while fetching
 * - Handles errors and allows retrying
 */
export function RegionPicker({
  className,
  hasError,
  htmlForId,
  initialRegion,
  isEnabled = true,
  isLoading = false,
  isFetching = true,
  isLoopingEnabled = true,
  items,
  label,
  onRefetchRegions,
  onSelectRegion,
}: IRegionPickerProps) {
  const t = useTranslations('DesignSystemComponents.RegionsPicker');
  const tLabel = label || t('label');
  const isSingleRegionAvailable = items?.length === 1;

  const [isOpen, setIsOpen] = useState(false);

  // If only one region is available, set it automatically
  useEffect(() => {
    if (isSingleRegionAvailable && items && items.length === 1) {
      onSelectRegion(items[0] ?? null);
    }
  }, [isSingleRegionAvailable, items, onSelectRegion]);

  useEffect(() => {
    const handleOnKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((newOpen) => !newOpen);
      }
    };

    document.addEventListener('keydown', handleOnKeyDown);
    return () => document.removeEventListener('keydown', handleOnKeyDown);
  }, []);

  const handleUpdateRegion = (id: string) => {
    const region = items?.find((item) => item.id === id) || null;
    onSelectRegion(id === initialRegion?.id ? null : region);
    setIsOpen(false);
  };

  const handleSearchRegionInput = (value: string, search: string) => {
    const region = items?.find((item) => item.id === value) || null;
    // @ts-ignore - We are sure it will be a string from the config.
    const searchLabel = (region?.label?.en as string) || '';

    if (searchLabel.toLowerCase().includes(search.toLowerCase())) {
      return 1;
    }

    return 0;
  };

  if (isLoading || hasError) {
    return (
      <RegionPickerFallback
        hasError={hasError}
        htmlForId={htmlForId}
        isLoading={isLoading}
        onRefetchRegions={onRefetchRegions}
      />
    );
  }

  return (
    <div className={className}>
      {isEnabled && !isSingleRegionAvailable ? (
        <label className="form-label" htmlFor={htmlForId}>
          <Text text={tLabel} />
        </label>
      ) : null}

      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={isOpen}
            className="h-12 w-full justify-between font-normal"
            disabled={!isEnabled || isSingleRegionAvailable}
            id={htmlForId}
            role="combobox"
            variant="outline"
          >
            {initialRegion?.logoUrl ? (
              <Image
                // @ts-ignore
                alt={`The flag for ${initialRegion?.label?.en}`}
                className="h-6 w-8 shrink-0 overflow-hidden rounded-sm"
                height={8}
                src={initialRegion.logoUrl}
                width={8}
              />
            ) : (
              <FlagIcon
                className="!h-6 !w-8 shrink-0 rounded-md fill-gray-200"
                id="global"
                size={180}
              />
            )}

            <Text
              className="w-full ps-2 text-start"
              text={initialRegion?.label || tLabel}
            />

            <span className="flex flex-row items-center gap-2">
              <kbd
                className="
                  bg-muted text-text-caption pointer-events-none inline-flex h-6
                  select-none items-center gap-1 rounded-sm border
                  px-1.5 font-mono text-[10px] font-medium opacity-100
                "
              >
                <span className="text-xs">⌘</span>
                <span>K</span>
              </kbd>

              <ChevronsUpDownIcon className="opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          className="min-w-[312px] max-w-full p-0 lg:min-w-[390px]"
        >
          <Command filter={handleSearchRegionInput} loop={isLoopingEnabled}>
            <CommandInput className="h-8" placeholder={t('placeholder')} />
            <CommandList>
              {isFetching ? (
                <CommandLoading>
                  <div className="flex w-full items-center justify-between pe-3 font-normal">
                    <Text
                      className="w-full ps-2 text-start"
                      text={t('loadingRegions')}
                    />
                    <CircularProgressLoader className="size-5 fill-primary-500 opacity-80" />
                  </div>
                </CommandLoading>
              ) : null}
              <CommandEmpty>{t('regionSearchNotFound')}</CommandEmpty>

              {items ? (
                <CommandGroup>
                  {items.map((region) => (
                    <CommandItem
                      key={region.id}
                      className="py-2"
                      onSelect={handleUpdateRegion}
                      value={region.id}
                    >
                      {region?.logoUrl ? (
                        <Image
                          // @ts-ignore
                          alt={t('flagAltText', { region: region?.label?.en })}
                          className="h-6 w-8 shrink-0 overflow-hidden rounded-sm"
                          height={8}
                          src={region.logoUrl}
                          width={8}
                        />
                      ) : (
                        <FlagIcon
                          className="!h-6 !w-8 shrink-0 rounded-md fill-gray-200"
                          id="global"
                          size={180}
                        />
                      )}

                      <Text className="ps-2" text={region.label} />
                      <CheckIcon
                        className={cn(
                          'ml-auto',
                          initialRegion?.identifier === region.id
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
